import { Schema } from "effect";
import { utf8ByteLength } from "./utf8";

export const DesktopWorkspaceBoundaryParseOptions = {
  errors: "all",
  onExcessProperty: "error",
  reportInput: false,
} as const;

export const DESKTOP_WORKSPACE_GRANT_ID_PATTERN = /^wrg_[0-9a-f]{32}$/u;
export const DESKTOP_WORKSPACE_MAX_DISPLAY_PATH_BYTES = 4_096;

const hasControlCharacter = (value: string): boolean => {
  for (const character of value) {
    const codePoint = character.codePointAt(0) ?? 0;
    if (codePoint <= 0x1f || (codePoint >= 0x7f && codePoint <= 0x9f)) return true;
  }
  return false;
};

/** Process-scoped selector for a Rust-owned directory descriptor. */
export const DesktopWorkspaceGrantIdSchema = Schema.String.check(
  Schema.makeFilter(
    (value) =>
      DESKTOP_WORKSPACE_GRANT_ID_PATTERN.test(value) ||
      "must be a canonical desktop workspace grant identifier",
  ),
);

/** Presentation metadata only. Native commands never accept this path as authority. */
export const DesktopWorkspaceDisplayPathSchema = Schema.String.check(
  Schema.isMinLength(1),
  Schema.makeFilter(
    (value) =>
      (!hasControlCharacter(value) &&
        utf8ByteLength(value) <= DESKTOP_WORKSPACE_MAX_DISPLAY_PATH_BYTES) ||
      `must be a display path of at most ${DESKTOP_WORKSPACE_MAX_DISPLAY_PATH_BYTES} UTF-8 bytes`,
  ),
);

export const DesktopWorkspaceRootGrantSchema = Schema.Struct({
  version: Schema.Literal(1),
  grantId: DesktopWorkspaceGrantIdSchema,
  displayPath: DesktopWorkspaceDisplayPathSchema,
});

export type DesktopWorkspaceGrantId = typeof DesktopWorkspaceGrantIdSchema.Type;
export type DesktopWorkspaceRootGrant = typeof DesktopWorkspaceRootGrantSchema.Type;
export type DesktopWorkspaceRootGrantEncoded = typeof DesktopWorkspaceRootGrantSchema.Encoded;

export const decodeDesktopWorkspaceRootGrant = Schema.decodeUnknownSync(
  DesktopWorkspaceRootGrantSchema,
  DesktopWorkspaceBoundaryParseOptions,
);
export const encodeDesktopWorkspaceRootGrant = Schema.encodeSync(
  DesktopWorkspaceRootGrantSchema,
  DesktopWorkspaceBoundaryParseOptions,
);
